export interface AviReportMetadata {
  uuid: string;
  package: string;
  status: string;
  phase?: number | null;
  imo: string;
  vesselName?: string | null;
  title?: string | null;
  [key: string]: unknown;
}

export interface AviIdentification {
  shipownerIdentified?: boolean | null;
  managerIdentified?: boolean | null;
  confidenceLevel?: string | null;
  shipownerConfidenceLevel?: string | null;
  shipmanagerConfidenceLevel?: string | null;
  ownershipStatus?: string | null;
  [key: string]: unknown;
}

export interface AviRecord {
  [key: string]: unknown;
}

export interface AviEvent extends AviRecord {
  description?: string | null;
  event_date?: string | null;
  date_precision?: string | null;
  location_name?: string | null;
  geometry?: { type?: string | null; coordinates?: number[] | null } | null;
  location_precision?: string | null;
  location_basis?: string | null;
  source_urls?: string[];
}

export interface AviRisk extends AviRecord {
  risk?: string | boolean | null;
  research_status?: string | null;
  why?: string | null;
  events: AviEvent[];
  urls: string[];
}

export interface AviProjection {
  imo: string;
  vesselName: string | null;
  identification: AviIdentification;
  shipOwner: AviRecord;
  shipManager: AviRecord;
  ownership: AviRecord[];
  ubos: AviRecord[];
  individuals: AviRecord[];
  group: AviRecord;
  vesselRisk: AviRisk;
  entityRisk: AviRisk[];
  sources: string[];
  [key: string]: unknown;
}

export interface AviReportResponse {
  report: AviReportMetadata;
  avi: AviProjection;
}

const asRecord = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};

const asArray = <T = any>(value: unknown): T[] => Array.isArray(value) ? value as T[] : [];

const normalizeRisk = (value: unknown): AviRisk => {
  const risk = asRecord(value);
  return { ...risk, events: asArray<AviEvent>(risk['events']), urls: asArray<string>(risk['urls']) };
};

export function normalizeAviReport(response: unknown): AviReportResponse {
  const root = asRecord(response);
  const report = asRecord(root['report']);
  const avi = asRecord(root['avi']);
  return {
    report: {
      ...report,
      uuid: String(report['uuid'] ?? ''),
      package: String(report['package'] ?? ''),
      status: String(report['status'] ?? ''),
      imo: String(report['imo'] ?? avi['imo'] ?? ''),
      phase: typeof report['phase'] === 'number' ? report['phase'] : null,
      vesselName: typeof report['vesselName'] === 'string' ? report['vesselName'] : null,
      title: typeof report['title'] === 'string' ? report['title'] : null
    },
    avi: {
      ...avi,
      imo: String(avi['imo'] ?? report['imo'] ?? ''),
      vesselName: typeof avi['vesselName'] === 'string' ? avi['vesselName'] : null,
      identification: asRecord(avi['identification']),
      shipOwner: asRecord(avi['shipOwner']),
      shipManager: asRecord(avi['shipManager']),
      ownership: asArray(avi['ownership']),
      ubos: asArray(avi['ubos']),
      individuals: asArray(avi['individuals']),
      group: asRecord(avi['group']),
      vesselRisk: normalizeRisk(avi['vesselRisk']),
      entityRisk: asArray(avi['entityRisk']).map(normalizeRisk),
      sources: asArray<string>(avi['sources'])
    }
  };
}
